import React, { useState } from 'react';
import AnimatedLogo from './AnimatedLogo';

interface Props { onLogin: (session: any) => void; }

/* ── Theme ─────────────────────────────────────────────────────────────── */
function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem('ce_demo_theme') !== 'light');
  const toggle = () => setDark(d => {
    const next = !d;
    localStorage.setItem('ce_demo_theme', next ? 'dark' : 'light');
    return next;
  });
  return { dark, toggle };
}

function tokens(dark: boolean) {
  return dark ? {
    bg:           '#000000',
    bg2:          '#0f0f1a',
    card:         'rgba(255,255,255,0.04)',
    cardHover:    'rgba(255,255,255,0.07)',
    border:       'rgba(255,255,255,0.08)',
    borderHover:  'rgba(99,102,241,0.4)',
    text:         '#f8fafc',
    muted:        '#94a3b8',
    dim:          '#64748b',
    navBg:        'rgba(0,0,0,0.85)',
    btnSec:       'rgba(255,255,255,0.06)',
    btnSecBorder: 'rgba(255,255,255,0.12)',
    themeBtnBg:   'rgba(255,255,255,0.06)',
    shadow:       '0 20px 60px rgba(0,0,0,0.5)',
    gridLine:     'rgba(99,102,241,0.05)',
    glow:         'rgba(99,102,241,0.25)',
  } : {
    bg:           '#f0f4ff',
    bg2:          '#e8eef8',
    card:         'rgba(255,255,255,0.85)',
    cardHover:    'rgba(255,255,255,0.97)',
    border:       'rgba(15,23,42,0.10)',
    borderHover:  'rgba(99,102,241,0.35)',
    text:         '#0f172a',
    muted:        '#475569',
    dim:          '#64748b',
    navBg:        'rgba(240,244,255,0.9)',
    btnSec:       'rgba(15,23,42,0.06)',
    btnSecBorder: 'rgba(15,23,42,0.15)',
    themeBtnBg:   'rgba(15,23,42,0.06)',
    shadow:       '0 8px 32px rgba(15,23,42,0.12)',
    gridLine:     'rgba(99,102,241,0.08)',
    glow:         'rgba(99,102,241,0.12)',
  };
}

/* ── SVG Icons ─────────────────────────────────────────────────────────── */
const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.94a16 16 0 0 0 6.06 6.06l1.06-1.06a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/>
  </svg>
);

const SunIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

/* Demo-specific SVG icons */
const VoicebotIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
    <circle cx="19" cy="5" r="2" fill="currentColor" strokeWidth="0" opacity="0.8"/>
    <path d="M19 3V1M21 5h2M19 7v2M17 5h-2" strokeWidth="1.2"/>
  </svg>
);

const AgentIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
  </svg>
);

const SupervisorIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
    <polyline points="7 8 10 11 13 9 17 13" strokeWidth="1.8"/>
    <circle cx="17" cy="13" r="1.5" fill="currentColor" strokeWidth="0"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    <circle cx="9" cy="12" r="0.8" fill="currentColor" strokeWidth="0"/>
    <circle cx="12" cy="12" r="0.8" fill="currentColor" strokeWidth="0"/>
    <circle cx="15" cy="12" r="0.8" fill="currentColor" strokeWidth="0"/>
  </svg>
);

const AdminIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

/* ── Demo tile definitions ──────────────────────────────────────────────── */
const DEMOS = [
  {
    role:        'voicebot',
    Icon:        VoicebotIcon,
    gradient:    'linear-gradient(135deg, #6366f1, #8b5cf6)',
    glowColor:   'rgba(99,102,241,0.3)',
    iconBg:      'rgba(99,102,241,0.15)',
    iconBorder:  'rgba(99,102,241,0.3)',
    badgeText:   'Self-Service',
    title:       'AI VoiceBot Wizard',
    subtitle:    'Build & test in under 2 minutes',
    desc:        'Create your own AI voice assistant, train it on your knowledge base, and watch it handle real conversations — ending with a live call to your mobile.',
    note:        null,
  },
  {
    role:        'agent',
    Icon:        AgentIcon,
    gradient:    'linear-gradient(135deg, #2563eb, #06b6d4)',
    glowColor:   'rgba(37,99,235,0.3)',
    iconBg:      'rgba(37,99,235,0.12)',
    iconBorder:  'rgba(37,99,235,0.25)',
    badgeText:   'Live SIP Calls',
    title:       'Agent Portal Demo',
    subtitle:    'Handle calls like a real agent',
    desc:        'Experience the full agent interface — WebRTC softphone, queue management, AI call assist with live transcription, and CRM screen-pop on every call.',
    note:        '30-second call limit on demo',
  },
  {
    role:        'supervisor',
    Icon:        SupervisorIcon,
    gradient:    'linear-gradient(135deg, #0891b2, #10b981)',
    glowColor:   'rgba(8,145,178,0.3)',
    iconBg:      'rgba(8,145,178,0.12)',
    iconBorder:  'rgba(8,145,178,0.25)',
    badgeText:   'Real-Time Monitoring',
    title:       'Supervisor Dashboard',
    subtitle:    'Full visibility over your team',
    desc:        'Live wallboard, agent state cards, queue KPIs, call whisper and barge-in. Analytics and reporting with CSV export — everything a supervisor needs.',
    note:        null,
  },
  {
    role:        'whatsapp',
    Icon:        WhatsAppIcon,
    gradient:    'linear-gradient(135deg, #25d366, #128c7e)',
    glowColor:   'rgba(37,211,102,0.3)',
    iconBg:      'rgba(37,211,102,0.12)',
    iconBorder:  'rgba(37,211,102,0.25)',
    badgeText:   'Messaging AI',
    title:       'WhatsApp & Chatbot',
    subtitle:    'AI-powered business messaging',
    desc:        'See how the AI chatbot handles WhatsApp conversations, escalates to a live agent with full chat history, and routes across multiple business numbers.',
    note:        null,
  },
];

/* ── Component ─────────────────────────────────────────────────────────── */
export default function DemoHub({ onLogin }: Props) {
  const { dark, toggle } = useTheme();
  const t = tokens(dark);
  const [launching, setLaunching] = useState<string | null>(null);
  const [error, setError]         = useState('');

  const launch = async (role: string) => {
    if (role === 'voicebot')  { window.location.href = '/demo'; return; }
    if (role === 'whatsapp')  { window.location.href = '/whatsapp-demo'; return; }
    setLaunching(role);
    setError('');
    try {
      const demo_token = sessionStorage.getItem('demo_token') || '';
      const res  = await fetch('/api/demo/instant-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, demo_token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      localStorage.setItem('token', data.token);
      const sess = { token: data.token, user: data.user, sipPassword: data.sipPassword || null, sipUsername: data.sipUsername || null, dids: data.dids || [] };
      localStorage.setItem('ce_session', JSON.stringify(sess));
      onLogin(sess);
    } catch (e: any) {
      setError(e.message || 'Failed to launch demo. Please try again.');
      setLaunching(null);
    }
  };

  const gridBg = `linear-gradient(${t.gridLine} 1px, transparent 0), linear-gradient(90deg, ${t.gridLine} 1px, transparent 0)`;

  return (
    <div style={{
      minHeight: '100vh',
      background: t.bg,
      backgroundImage: gridBg,
      backgroundSize: '64px 64px',
      fontFamily: 'Inter, system-ui, sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Radial hero glow */}
      <div style={{
        position: 'absolute', top: '-8%', left: '50%', transform: 'translateX(-50%)',
        width: '80%', height: '55%', borderRadius: '50%', pointerEvents: 'none',
        background: `radial-gradient(ellipse at center, ${t.glow} 0%, transparent 65%)`,
      }} />
      <div style={{
        position: 'absolute', bottom: '-5%', right: '-5%', width: '35%', height: '40%',
        borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, rgba(139,92,246,0.1) 0%, transparent 65%)',
      }} />
      <div style={{
        position: 'absolute', top: '30%', left: '-5%', width: '25%', height: '30%',
        borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, rgba(6,182,212,0.07) 0%, transparent 65%)',
      }} />

      {/* ── Nav ──────────────────────────────────────────────────── */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 40px',
        background: t.navBg,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${t.border}`,
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <AnimatedLogo width={40} />
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'rgba(255,255,255,0.92)', letterSpacing: 0.2, fontFamily: 'Inter, Arial, sans-serif' }}>
                CallEssential
              </div>
              <div style={{ fontSize: 9.5, fontWeight: 500, color: 'rgba(255,255,255,0.45)', letterSpacing: 0.3, fontFamily: 'Inter, Arial, sans-serif' }}>
                powered by Synergytalk
              </div>
            </div>
          <span style={{
            marginLeft: 8, fontSize: 10, fontWeight: 500, color: '#a5b4fc',
            background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)',
            borderRadius: 999, padding: '2px 8px', letterSpacing: '0.05em', textTransform: 'uppercase',
          }}>Demo</span>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <a href="https://www.callessential.cc/book-demo" target="_blank" rel="noreferrer" style={{
            fontSize: 13, color: t.muted, textDecoration: 'none', fontWeight: 500,
            padding: '7px 14px', borderRadius: 8,
            background: t.btnSec, border: `1px solid ${t.btnSecBorder}`,
          }}>Book a Demo</a>
          <button onClick={toggle} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '7px 12px', borderRadius: 8,
            background: t.themeBtnBg, border: `1px solid ${t.border}`,
            color: t.muted, cursor: 'pointer', fontSize: 12, fontWeight: 500,
          }}>
            {dark ? <SunIcon /> : <MoonIcon />}
            {dark ? 'Light' : 'Dark'}
          </button>
          <button onClick={() => { sessionStorage.removeItem('demo_auth'); sessionStorage.removeItem('demo_token'); window.location.reload(); }} style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 500,
            background: 'transparent', border: `1px solid ${t.border}`,
            color: t.dim, cursor: 'pointer',
          }}>Sign out</button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <div style={{
        textAlign: 'center', padding: '72px 24px 56px',
        position: 'relative', zIndex: 5,
      }}>
        <div style={{
          display: 'inline-block', marginBottom: 20,
          background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.28)',
          borderRadius: 999, padding: '6px 18px',
          fontSize: 11, color: '#a5b4fc', fontWeight: 500,
          letterSpacing: '0.07em', textTransform: 'uppercase',
        }}>Live Demo Environment — Real Infrastructure</div>

        <h1 style={{
          fontSize: 'clamp(32px,5vw,52px)', fontWeight: 800, margin: '0 0 18px',
          lineHeight: 1.15,
          background: dark
            ? 'linear-gradient(135deg, #f8fafc 30%, #818cf8 65%, #a78bfa 100%)'
            : 'linear-gradient(135deg, #0f172a 20%, #6366f1 60%, #8b5cf6 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          Choose Your Demo<br/>Experience
        </h1>

        <p style={{
          fontSize: 17, color: t.muted, margin: '0 auto',
          maxWidth: 520, lineHeight: 1.6,
        }}>
          Explore every feature on live infrastructure.<br/>No setup, no commitment, no credit card.
        </p>
      </div>

      {error && (
        <div style={{
          maxWidth: 560, margin: '0 auto 24px',
          padding: '12px 20px', textAlign: 'center',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 10, color: '#fca5a5', fontSize: 13,
        }}>{error}</div>
      )}

      {/* ── 4 Demo tiles ─────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 24,
        maxWidth: 1140,
        margin: '0 auto',
        padding: '0 32px 60px',
        position: 'relative', zIndex: 5,
      }}>
        {DEMOS.map(d => (
          <DemoCard
            key={d.role} demo={d} t={t}
            loading={launching === d.role}
            disabled={!!launching && launching !== d.role}
            onLaunch={() => launch(d.role)}
          />
        ))}
      </div>

      {/* ── Admin panel link ──────────────────────────────────────── */}
      <div style={{ textAlign: 'center', paddingBottom: 60, position: 'relative', zIndex: 5 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 12,
          background: t.card,
          backdropFilter: 'blur(16px)',
          border: `1px solid ${t.border}`,
          borderRadius: 14, padding: '16px 28px',
        }}>
          <span style={{ color: t.dim }}>
            <AdminIcon />
          </span>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 13, color: t.muted, marginBottom: 2 }}>Looking for the Admin Panel?</div>
            <a href="/admindemo" style={{
              fontSize: 13, fontWeight: 600, color: '#818cf8', textDecoration: 'none',
            }}>Access Admin Panel Demo → (requires separate credentials)</a>
          </div>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <div style={{
        borderTop: `1px solid ${t.border}`,
        padding: '24px 40px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: t.navBg, backdropFilter: 'blur(20px)',
        fontSize: 12, color: t.dim, position: 'relative', zIndex: 5,
        flexWrap: 'wrap', gap: 10,
      }}>
        <span>© 2025 SynergyTalk · CallEssential</span>
        <div style={{ display: 'flex', gap: 20 }}>
          <a href="mailto:sales@synergytalk.ae" style={{ color: '#818cf8', textDecoration: 'none' }}>sales@synergytalk.ae</a>
          <a href="https://www.callessential.cc" target="_blank" rel="noreferrer" style={{ color: '#818cf8', textDecoration: 'none' }}>callessential.cc</a>
        </div>
      </div>
    </div>
  );
}

/* ── DemoCard sub-component ──────────────────────────────────────────────── */
function DemoCard({ demo: d, t, loading, disabled, onLaunch }: {
  demo: typeof DEMOS[number]; t: ReturnType<typeof tokens>;
  loading: boolean; disabled: boolean; onLaunch: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? t.cardHover : t.card,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${hovered ? t.borderHover : t.border}`,
        borderRadius: 20,
        padding: '30px 28px',
        display: 'flex', flexDirection: 'column',
        transition: 'all 0.25s ease',
        transform: hovered && !disabled ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered && !disabled ? `0 24px 60px rgba(0,0,0,0.3), 0 0 0 1px ${d.glowColor}` : '0 4px 20px rgba(0,0,0,0.2)',
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {/* Icon + badge */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22 }}>
        <div style={{
          width: 58, height: 58, borderRadius: 16,
          background: hovered ? d.gradient : d.iconBg,
          border: `1px solid ${d.iconBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: hovered ? '#fff' : d.iconBorder.replace('0.25)', '0.9)'),
          transition: 'all 0.25s',
        } as any}>
          <d.Icon />
        </div>
        <span style={{
          fontSize: 10, fontWeight: 600,
          background: d.iconBg, border: `1px solid ${d.iconBorder}`,
          color: d.iconBorder.replace('0.25)', '0.85)'),
          borderRadius: 999, padding: '3px 10px',
          textTransform: 'uppercase', letterSpacing: '0.06em',
          whiteSpace: 'nowrap',
        }}>{d.badgeText}</span>
      </div>

      {/* Text */}
      <div style={{ fontSize: 19, fontWeight: 700, color: t.text, marginBottom: 5 }}>{d.title}</div>
      <div style={{
        fontSize: 12, fontWeight: 600, marginBottom: 12,
        background: d.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>{d.subtitle}</div>
      <div style={{ fontSize: 13, color: t.muted, lineHeight: 1.65, flex: 1 }}>{d.desc}</div>

      {d.note && (
        <div style={{
          marginTop: 12, fontSize: 11, color: '#f59e0b',
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 6, padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {d.note}
        </div>
      )}

      {/* Launch button */}
      <button
        onClick={onLaunch}
        disabled={loading || disabled}
        style={{
          marginTop: 22, width: '100%', padding: '12px 0',
          background: loading ? 'rgba(99,102,241,0.3)' : d.gradient,
          border: 'none', borderRadius: 10,
          color: '#fff', fontSize: 14, fontWeight: 600,
          cursor: loading || disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'opacity 0.2s, box-shadow 0.2s',
          boxShadow: hovered && !disabled ? `0 8px 24px ${d.glowColor}` : 'none',
          fontFamily: 'inherit',
        }}
      >
        {loading ? 'Launching…' : (
          <>Launch {d.title} <ArrowIcon /></>
        )}
      </button>
    </div>
  );
}
